import request from "@/http"
import http from "@/http/http"
import type { ProfileBasicItem, AreaCoutTreeItem } from "@/api"
import { downloadFile } from "@/util/util"

/**
 *
 * 获取poi分类
 */
export interface MapCategoryVO {
  id: string
  name: string
  unlocked: boolean
  children: any[]
}
export async function getPoiCates(): Promise<MapCategoryVO[]> {
  try {
    const result = await http.get("/nearbyEcology/nearby/config/customMapTag")
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface PointNearByArgs {
  poiId: string
  brandId: string
  cityId: string
  moduleType: string
  longitude: number
  latitude: number
  radius: number
  tagIds: string[]
  page: number
  limit: number
}
export interface CategoryDetailVO {
  address: string
  distance: number
  latitude: number
  longitude: number
  name: string
  poiId: string
  categoryType: string // 地图二级分类类型
  visitFlowCount: number // 总到访客流
  residentFlowCount: number // 常驻客流
  aoiPolygon: number[][] | null // aoi围栏
}
export interface PoiVO {
  categoryName: string
  count: string | number
  poiDetailList: CategoryDetailVO[]
  // 分类id
  categoryId: string
  // 子标签id
  subTagIds?: string[]
  // 占辐射半径3km比例
  ratioToDistrict: number
  // 本地添加的
  isShow?: boolean
  color?: string
}

/**
 *
 * 获取poi列表
 */
export async function getPoiNearby(params: PointNearByArgs) {
  try {
    const result = await http.post<PoiVO[]>("/nearbyEcology/nearby/category", params)
    return result?.data
  } catch (error: any) {
    // return []
    throw error && error.response
  }
}

export interface PointNearbyDetailArgs {
  cityId: string // 城市id
  brandId: string // 品牌id
  categoryId?: string // 分类id
  tagIds: string[]
  moduleType: string // 模块名称
  latitude: number // 中心点纬度
  longitude: number // 中心点经度
  page: number // 页码
  limit: number // 每页数量
  sortType?: string // 生态配套详情排序方式
  isAsc?: boolean // true-升序，false-降序
  radius: number // 半径
}

export interface PoiDetailVO {
  name: string
  address: string
  // 距离-米
  distance: number
  latitude: number
  longitude: number
  poiId: string
  // 总到访
  visitFlowCount: number
  // 常驻客流
  residentFlowCount: number
  // 对应地图二级分类
  categoryType: string
  // aoi围栏
  aoiPolygon: number[][]
  // 小区住户数
  residentCount: number
  // 房价
  housePrice: number
}

/**
 * @name 获取配套详情信息
 *
 * @export
 * @param {PointNearbyDetailArgs} params
 * @returns
 */
export async function getNearbyPoiDetail(params: PointNearbyDetailArgs) {
  try {
    const result = await http.post<PoiDetailVO[]>("/nearbyEcology/nearby/category/poi/list", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

type ExportListArgs = PointNearbyDetailArgs & {
  fileName: string
}

/**
 * 导出分类列表数据
 */
export async function exportNearbyPoiList(params: ExportListArgs): Promise<any> {
  try {
    const { fileName = "列表数据", ...others } = params
    const result = await request.post<any>("/nearbyEcology/nearby/export", others, {
      responseType: "blob",
    })
    downloadFile(result.data, `${fileName}.xlsx`)
    return result.status
  } catch (error) {
    throw error
  }
}

type ExportDetailArgs = PointProfileArgs & {
  fileName: string
}

/**
 * 导出单个店铺数据详情
 */
export async function exportPointDetail(params: ExportDetailArgs): Promise<any> {
  try {
    const { fileName = "数据", ...others } = params
    const result = await request.post<any>("/nearbyEcology/nearby/exportDetail", others, {
      responseType: "blob",
    })
    downloadFile(result.data, `${fileName}.xlsx`)
    return result.status
  } catch (error) {
    throw error
  }
}

export interface PointProfileArgs {
  cityId: string // 城市id
  brandId: string // 品牌id
  moduleType: string // 模块名称
  poiId: string // poi ids
  latitude: number // 某店中心点纬度
  longitude: number // 某店中心点经度
  categoryType: string // 分类类型
  centralLatitude?: number | null // 网点中心点纬度
  centralLongitude?: number | null // 网点中心点经度
}

interface HourBasicItem {
  /**
   * 小时
   */
  hour: string
  /**
   * 人数
   */
  count: number
}

export interface EcologyInfo {
  // 名称
  name: string
  // 生态类型
  ecologyType: string
  // 地址
  address: string
  // 开业时间
  openingDate: any
  // 联系电话
  phone: string
  // 注册资本（元）
  capital: number
  // 纳税识别号
  taxNum: string
}

export interface PointProfile {
  // 名字
  name: string
  // 数据统计时间
  dataDate: string
  // 开业时间
  openingDate: string
  // 地址
  address: string
  // 纬度
  latitude: number
  // 经度
  longitude: number
  // 上月客流
  lastMonthFlowCount: number
  // 上月月度平均分时指数
  hourRatio: HourBasicItem[]
  // 年龄分布
  age: ProfileBasicItem[]
  // 消费能力
  consumption: ProfileBasicItem[]
  // 住房等级指数
  housePrice: ProfileBasicItem[]
  // 有车指数
  carAssets: ProfileBasicItem[]
  // 生态信息
  ecologyInfo: EcologyInfo
  // 生态信息类型
  ecoType: string | null
  // 距离-米
  distance: number
  storeName: string
  residentFlowCount: number
}

/**
 * @name 获取指定地点的poi画像
 *
 * @export
 * @param {PointProfileArgs} params
 * @returns
 */
export async function getPointProfile(params: PointProfileArgs): Promise<PointProfile> {
  try {
    const result = await http.post<PointProfile>("/nearbyEcology/nearby/category/poi", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface GuestPortraitArgs {
  lat: number
  lon: number
  radius: number
  industryAnalyses: string[]
  profiles: string[]
}

interface StoreItemVO {
  address: string
  categories: string
  lat: number
  lon: number
  name: string
  populationLeastTime: string
  populationMostTime: string
  visitPopulation: string
  weekendPopulation: string
  workDaypopulation: string
}

interface AnalyseDetailVO {
  count: number
  name: string
  list: StoreItemVO[]
}

interface IndustryAnalyseListVO {
  count: number
  name: string
  analyseDetail: AnalyseDetailVO[]
  storeList: StoreItemVO[]
}

interface OnlinePreferenceItemVO {
  id: string
  latitude: number
  longitude: number
  name: string
  ratio: number
}

export interface OnlinePreferenceVO {
  name: string
  shortName: string
  tagKeyId: string
  items: OnlinePreferenceItemVO[]
}

export interface PeopleCountVO {
  livingCount: number
  workingCount: number
  age: OnlinePreferenceItemVO[]
  carAssets: OnlinePreferenceItemVO[]
  consumption: OnlinePreferenceItemVO[]
  gender: OnlinePreferenceItemVO[]
  marriage: OnlinePreferenceItemVO[]
}

interface GuestPortraitResult {
  area: string
  buyPreference: string[]
  industryAnalyseList: IndustryAnalyseListVO[]
  offLineVisit: string
  onlinePreference: OnlinePreferenceVO[]
  peopleCount: PeopleCountVO
}

/**
 * 获取客群画像
 */
export async function getGuestPortrait(params: GuestPortraitArgs): Promise<GuestPortraitResult> {
  try {
    const result = await http.post<GuestPortraitResult>(
      "/nearbyEcology/nearby/businessCircle",
      params,
    )

    return result?.data
  } catch (error) {
    throw error
  }
}

interface GetCustomPoiArgs {
  // 沙盘ID
  sandTableId: string
  // 区域ID
  areaCode: string
  // 品牌id
  brandId: string
  // 每页条数
  pageSize?: number
  // 页数
  pageNum?: number
}

export interface CustomPoiVO {
  // 自定义点位ID
  customPoiId: string
  // 点位名称
  poiName: string
  // 地址
  address: string
  // 纬度
  latitude: string
  // 经度
  longitude: string
  // 删除标识 0 - 正常；1 - 已删除
  delFlag: string
  // 客流评分
  flowRatio: number
}

interface GetCustomPoiResult {
  rows: CustomPoiVO[]
  total: number
}

// 自定义点位列表查询
export async function getCustomPoiList(args: GetCustomPoiArgs): Promise<GetCustomPoiResult> {
  try {
    const { pageNum, pageSize, ...others } = args
    const param = pageNum && pageSize ? `?pageNum=${pageNum}&pageSize=${pageSize}` : ""
    const result = await http.post<GetCustomPoiResult>(
      `/business/sandTable/uploadPoi/list${param}`,
      others,
    )
    return result as any
  } catch (error) {
    throw error
  }
}

interface DeleteCustomArgs {
  // 沙盘ID
  sandTableId: string
  // 自定义点位ID
  customPoiId: string
}

// 自定义点位删除
export async function deleteCustomPoi(args: DeleteCustomArgs): Promise<boolean> {
  try {
    const result = await request.delete<boolean>(`/business/sandTable/uploadPoi`, {
      data: args,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface UploadPoiAreaCountArgs {
  // 沙盘ID
  sandTableId: string
  // 品牌id
  brandId: string
}

// 统计品牌地区自定义网点数量
export async function getUploadPoiAreaCount(
  args: UploadPoiAreaCountArgs,
): Promise<AreaCoutTreeItem[]> {
  try {
    const result = await http.get<AreaCoutTreeItem[]>(`/business/poi/areaCount/uploadPoi`, {
      params: args,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}
